import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Bookmark, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ListingCard from "@/components/ListingCard";
import { toast } from "sonner";
import { CATEGORIES, timeAgo } from "@/lib/constants";

export default function Favorites() {
    const [data, setData] = useState({ listings: [], posts: [] });
    const [saved, setSaved] = useState([]);
    const navigate = useNavigate();

    async function load() {
        try {
            const [{ data: favs }, { data: srch }] = await Promise.all([
                api.get("/favorites"),
                api.get("/saved-searches"),
            ]);
            setData(favs);
            setSaved(srch);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }
    useEffect(() => {
        load();
    }, []);

    async function delSearch(s) {
        await api.delete(`/saved-searches/${s.search_id}`);
        setSaved((c) => c.filter((x) => x.search_id !== s.search_id));
    }

    return (
        <div className="min-h-screen bg-onyx-900">
            <div
                className="sticky top-0 z-30 backdrop-blur-xl border-b border-onyx-600"
                style={{ background: "rgba(10,10,10,0.85)" }}
            >
                <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-2">
                    <button
                        onClick={() => navigate(-1)}
                        className="btn-ghost rounded-sm px-2.5 py-2"
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="headline text-lg flex items-center gap-2">
                        <Bookmark className="w-5 h-5" /> Saved
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 py-5">
                <section>
                    <div className="label-caps mb-3">Saved listings</div>
                    {data.listings.length === 0 ? (
                        <div className="surface rounded-md p-8 text-center text-onyx-500 text-sm">
                            No saved listings yet.
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {data.listings.map((l) => (
                                <ListingCard listing={l} key={l.listing_id} />
                            ))}
                        </div>
                    )}
                </section>

                <section className="mt-8">
                    <div className="label-caps mb-3">Saved posts</div>
                    {data.posts.length === 0 ? (
                        <div className="surface rounded-md p-8 text-center text-onyx-500 text-sm">
                            No saved posts yet.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {data.posts.map((p) => (
                                <Link
                                    to={`/community/${p.post_id}`}
                                    key={p.post_id}
                                    className="surface rounded-md p-3 block"
                                >
                                    <div className="headline text-sm">{p.title}</div>
                                    <div className="text-xs text-onyx-500 mt-1">
                                        {CATEGORIES.find((c) => c.value === p.category)?.label}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </section>

                <section className="mt-8">
                    <div className="label-caps mb-3">Saved searches</div>
                    {saved.length === 0 ? (
                        <div className="surface rounded-md p-8 text-center text-onyx-500 text-sm">
                            No saved searches yet. Save one from the Marketplace filter bar.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {saved.map((s) => (
                                <div
                                    key={s.search_id}
                                    className="surface rounded-md p-3 flex items-center gap-3"
                                >
                                    <div className="flex-1 min-w-0">
                                        <div className="headline text-sm truncate">{s.name}</div>
                                        <div className="text-xs text-onyx-500 mt-1 truncate">
                                            {Object.entries(s.filters || {})
                                                .filter(([, v]) => v)
                                                .map(([k, v]) => `${k}: ${v}`)
                                                .slice(0, 4)
                                                .join(" · ")}
                                        </div>
                                    </div>
                                    <div className="text-xs text-onyx-500 label-caps">
                                        {timeAgo(s.created_at)}
                                    </div>
                                    <button
                                        onClick={() => delSearch(s)}
                                        className="btn-ghost rounded-sm px-2 py-2 text-hellcat"
                                        data-testid={`del-search-${s.search_id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
